import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError, TimeoutError } from 'rxjs';
import { timeout, catchError } from 'rxjs/operators';

/** Default timeout for all HTTP requests (30 s). */
const DEFAULT_TIMEOUT_MS = 30_000;

/**
 * Endpoints that must NOT be subject to a short timeout because they hold
 * open a long-lived connection (SSE, streaming, file uploads).
 * Supports both plain strings (substring match) and RegExp.
 */
const SKIP_TIMEOUT_PATTERNS: Array<string | RegExp> = [
  '/documents/upload',
  /\/documents\/[^/]+\/version/,
];

/**
 * Endpoints that run a multi-step LLM chain (classify → plan → generate, etc.) and
 * need more room than the 30s default — especially against a local Ollama provider,
 * where each step alone can take 10-30s+ on consumer hardware vs. sub-second on Groq's
 * cloud infra. Gets its own longer tier rather than raising DEFAULT_TIMEOUT_MS, so the
 * 30s safety net still applies to every other (fast) endpoint.
 */
const LONG_TIMEOUT_MS = 240_000;
const LONG_TIMEOUT_PATTERNS: Array<string | RegExp> = ['/ai/chat'];

@Injectable()
export class TimeoutInterceptor implements HttpInterceptor {
  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const skip = SKIP_TIMEOUT_PATTERNS.some((pattern) =>
      pattern instanceof RegExp ? pattern.test(req.url) : req.url.includes(pattern),
    );

    if (skip) {
      return next.handle(req);
    }

    const isLongRunning = LONG_TIMEOUT_PATTERNS.some((pattern) =>
      pattern instanceof RegExp ? pattern.test(req.url) : req.url.includes(pattern),
    );
    const timeoutMs = isLongRunning ? LONG_TIMEOUT_MS : DEFAULT_TIMEOUT_MS;

    return next.handle(req).pipe(
      timeout(timeoutMs),
      catchError((err: unknown) => {
        if (err instanceof TimeoutError) {
          return throwError(
            () =>
              new HttpErrorResponse({
                status: 408,
                statusText: 'Request Timeout',
                error: { message: `Request timed out after ${timeoutMs / 1000}s` },
                url: req.url,
              }),
          );
        }
        return throwError(() => err);
      }),
    );
  }
}
